import React, { useState, useEffect, useRef, memo } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen, FileText, RefreshCw, Search, Building2 } from 'lucide-react';
import fileService from '../services/fileService';
import dataroomService from '../services/dataroomService';
import { useNavigate } from 'react-router-dom';
import StorageIndicator from './StorageIndicator';

const FolderTreeSidebar = memo(({ dataroomUuid, currentFolderUuid, onFolderSelect, storageStats, isOwner }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [datarooms, setDatarooms] = useState([]);
  const [selectedDataroom, setSelectedDataroom] = useState(null);
  const [loadingDatarooms, setLoadingDatarooms] = useState(true);

  // Fetch datarooms only once on mount
  useEffect(() => {
    fetchDatarooms();
  }, []);

  // Separate effect for home navigation event
  useEffect(() => {
    const handleResetToHome = () => {
      // Reset to show all files (root level)
      if (onFolderSelect) {
        onFolderSelect(null);
      }
    };

    window.addEventListener('resetToHome', handleResetToHome);
    return () => {
      window.removeEventListener('resetToHome', handleResetToHome);
    };
  }, [onFolderSelect]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSearchTerm('');
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  useEffect(() => {
    if (dataroomUuid) {
      fetchFolderTree();
    }
  }, [dataroomUuid]);

  // Separate effect for setting selected dataroom
  useEffect(() => {
    if (dataroomUuid && datarooms.length > 0) {
      const currentRoom = datarooms.find(d => d.uuid === dataroomUuid);
      if (currentRoom) {
        setSelectedDataroom(currentRoom);
      }
    }
  }, [dataroomUuid, datarooms]);

  const fetchDatarooms = async () => {
    setLoadingDatarooms(true);
    try {
      const response = await dataroomService.getMyDataroomsList();
      const dataroomsList = response.data || response.datarooms || [];
      // Filter out any null/undefined entries and ensure all have required fields
      const validDatarooms = dataroomsList.filter(d => d && d.uuid).map(d => ({
        ...d,
        name: d.name || d.dataroom_name || `Dataroom ${d.uuid.substring(0, 8)}`
      }));
      setDatarooms(validDatarooms);

      // Find and set current dataroom
      if (dataroomUuid) {
        const current = validDatarooms.find(d => d && d.uuid === dataroomUuid);
        if (current) {
          setSelectedDataroom(current);
        }
      }
    } catch (err) {
      console.error('Error fetching datarooms:', err);
    } finally {
      setLoadingDatarooms(false);
    }
  };

  const handleDataroomChange = (dataroom) => {
    if (dataroom && dataroom.uuid !== dataroomUuid) {
      setSelectedDataroom(dataroom);
      // Navigate to the new dataroom with correct URL structure
      navigate(`/datarooms/${dataroom.uuid}/files`);
    }
  };

  const fetchFolderTree = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fileService.getFoldersWithFileCounts(dataroomUuid);
      console.log('Folder tree API response:', response);

      // Handle different response structures
      let folderData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          folderData = response.data;
        } else if (response.data.folders && Array.isArray(response.data.folders)) {
          folderData = response.data.folders;
        }
      } else if (Array.isArray(response)) {
        folderData = response;
      } else if (response.folders && Array.isArray(response.folders)) {
        folderData = response.folders;
      }

      // The API already returns a nested structure, no need to build it
      setFolders(folderData);

      // Start with all folders closed by default
      setExpandedFolders(new Set());
    } catch (err) {
      console.error('Error fetching folder tree:', err);
      setError('Failed to load folder structure');
    } finally {
      setLoading(false);
    }
  };

  // Not needed anymore as API returns nested structure
  // Keeping for backward compatibility if needed
  const buildTreeStructure = (flatFolders) => {
    // If already nested structure, return as is
    if (flatFolders.length > 0 && 'folders' in flatFolders[0]) {
      return flatFolders;
    }

    // Otherwise build tree from flat structure (legacy support)
    const folderMap = {};
    const rootFolders = [];

    flatFolders.forEach(folder => {
      const id = folder.uuid || folder.id;
      folderMap[id] = {
        ...folder,
        id: id,
        folders: folder.folders || [],
        children: folder.children || []
      };
    });

    flatFolders.forEach(folder => {
      const id = folder.uuid || folder.id;
      const parentId = folder.parent_folder_uuid || folder.parent_uuid || folder.parent_id;

      if (parentId && folderMap[parentId]) {
        folderMap[parentId].children.push(folderMap[id]);
      } else {
        rootFolders.push(folderMap[id]);
      }
    });

    return rootFolders;
  };

  const toggleFolder = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    const isExpanding = !newExpanded.has(folderId);

    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);

    // Auto-scroll when expanding a folder
    if (isExpanding && scrollContainerRef.current) {
      setTimeout(() => {
        const folderElement = scrollContainerRef.current.querySelector(`[data-folder-id="${folderId}"]`);
        if (folderElement) {
          folderElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    }
  };

  // Helper function to build breadcrumb path by traversing up the tree
  const buildBreadcrumbPath = (targetFolder, allFolders = folders) => {
    console.log('buildBreadcrumbPath called with:', {
      targetFolder,
      allFolders,
      foldersLength: allFolders?.length
    });

    // Recursive function to find folder and build path
    const findFolderPath = (folderList, target, currentPath = []) => {
      for (const folder of folderList) {
        const folderId = folder.uuid || folder.id;
        const newPath = [...currentPath, {
          id: folderId,
          name: folder.folder_name || folder.name || folder.display_name,
          display_name: folder.folder_name || folder.name || folder.display_name,
          uuid: folder.uuid || folder.id
        }];

        if (folderId === (target.uuid || target.id)) {
          console.log('Found matching folder! Path:', newPath);
          return newPath;
        }

        const childFolders = folder.folders || folder.children || [];
        if (childFolders.length > 0) {
          const found = findFolderPath(childFolders, target, newPath);
          if (found) return found;
        }
      }
      return null;
    };

    const foundPath = findFolderPath(allFolders, targetFolder);
    console.log('buildBreadcrumbPath result:', foundPath);
    return foundPath || [];
  };

  const handleFolderClick = (folder, parentPath = []) => {
    const folderId = folder.uuid || folder.id;
    toggleFolder(folderId);
    if (onFolderSelect) {
      // Build breadcrumb path
      const breadcrumbs = buildBreadcrumbPath(folder);
      console.log('FolderTreeSidebar - handleFolderClick:', {
        folderId,
        folder,
        breadcrumbs
      });
      // Pass folder ID, folder object, and breadcrumbs
      onFolderSelect(folderId, folder, breadcrumbs);
    }
  };

  const renderFolder = (folder, level = 0, isLast = false, parentPath = []) => {
    const folderId = folder.uuid || folder.id;
    const isExpanded = expandedFolders.has(folderId);
    // Check for nested folders using 'folders' property from API
    const hasChildren = (folder.folders && folder.folders.length > 0) ||
                       (folder.children && folder.children.length > 0);
    const isSelected = currentFolderUuid === folderId;
    const fileCount = folder.files_count || folder.file_count || 0;
    // Use folder_name from API response
    const folderName = folder.folder_name || folder.name || folder.display_name || 'Unnamed Folder';
    // Get child folders from 'folders' property
    const childFolders = folder.folders || folder.children || [];

    return (
      <div key={folderId} className="relative" data-folder-id={folderId}>
        <div
          className={`relative flex items-center py-1.5 px-2 hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
          style={{ paddingLeft: `${level * 24 + 8}px` }}
        >
          {/* Active indicator */}
          {isSelected && (
            <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500" />
          )}

          {/* Tree lines */}
          {level > 0 && (
            <>
              {/* Vertical lines for parent levels */}
              {parentPath.map((isParentLast, idx) => (
                !isParentLast && (
                  <span
                    key={idx}
                    className="absolute border-l border-gray-200"
                    style={{
                      left: `${idx * 24 + 16}px`,
                      top: 0,
                      bottom: 0
                    }}
                  />
                )
              ))}
              {/* Vertical line for current level */}
              <span
                className="absolute border-l border-gray-200"
                style={{
                  left: `${(level - 1) * 24 + 16}px`,
                  top: 0,
                  height: isLast ? '16px' : '100%'
                }}
              />
              {/* Horizontal line connecting to folder */}
              <span
                className="absolute border-b border-gray-200"
                style={{
                  left: `${(level - 1) * 24 + 16}px`,
                  top: '16px',
                  width: '16px'
                }}
              />
            </>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) {
                toggleFolder(folderId);
              }
            }}
            className="mr-1 hover:bg-gray-100 rounded flex-shrink-0 flex items-center justify-center"
            style={{ width: '16px', height: '16px' }}
            type="button"
          >
            {hasChildren ? (
              isExpanded ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 6L8 10L12 6" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 4L10 8L6 12" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )
            ) : (
              <div className="w-4" />
            )}
          </button>

          <Folder size={16} className={`${isExpanded && hasChildren ? 'text-blue-500' : 'text-gray-400'} flex-shrink-0 mr-2`} />

          <span
            className="flex-1 text-sm cursor-pointer select-none truncate"
            onClick={() => handleFolderClick(folder, parentPath)}
            title={folderName}
          >
            {folderName}
            {fileCount > 0 && (
              <span className="ml-2 text-xs text-gray-500">({fileCount})</span>
            )}
          </span>
        </div>

        {isExpanded && hasChildren && (
          <div className="relative">
            {childFolders.map((child, index) =>
              renderFolder(
                child,
                level + 1,
                index === childFolders.length - 1,
                [...parentPath, isLast]
              )
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-64 bg-white border-r border-gray-100 flex flex-col" style={{ height: '100vh' }}>
      {/* Header: Dataroom Dropdown */}
      <div className="flex items-center gap-2 border-b border-gray-200 flex-shrink-0" style={{ padding: '6px 14px' }}>
        {loadingDatarooms ? (
          <div className="flex items-center justify-center py-2">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="relative flex-1" ref={dropdownRef}>
              <div
                className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 hover:bg-white"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <span className="text-sm font-medium text-gray-700 truncate">
                  {selectedDataroom ? (selectedDataroom.name || selectedDataroom.dataroom_name) : 'Select dataroom...'}
                </span>
                <ChevronDown size={14} className={`text-gray-400 ${showDropdown ? 'rotate-180' : ''}`} />
              </div>

            {showDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-hidden">
                <div className="p-2 border-b">
                  <div className="relative">
                    <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search datarooms..."
                      className="w-full pl-7 pr-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {datarooms
                    .filter(d => {
                      if (!searchTerm) return true;
                      const name = (d.name || d.dataroom_name || '').toLowerCase();
                      return name.includes(searchTerm.toLowerCase());
                    })
                    .map(dataroom => (
                      <div
                        key={dataroom.uuid}
                        className={`flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer ${
                          selectedDataroom && selectedDataroom.uuid === dataroom.uuid ? 'bg-blue-50 text-blue-700' : ''
                        }`}
                        onClick={() => {
                          handleDataroomChange(dataroom);
                          setShowDropdown(false);
                          setSearchTerm('');
                        }}
                      >
                        <Building2 size={14} className="text-gray-400 flex-shrink-0" />
                        <span className="truncate">{dataroom.name || dataroom.dataroom_name}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
            </div>

            <button
              className="hover:bg-gray-100 rounded flex-shrink-0"
              style={{ padding: '6px 14px' }}
              onClick={fetchFolderTree}
              title="Refresh folder tree"
            >
              <RefreshCw size={16} className="text-gray-500" />
            </button>
          </>
        )}
      </div>

      {/* Scrollable Area: All Files + Folder Tree */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto" style={{ scrollBehavior: 'smooth' }}>
        {/* All Files Section */}
        {!loading && !error && (
          <div className="p-3 border-b border-gray-100">
            <div className="mb-2">
              <div
                className={`relative flex items-center gap-2 py-1.5 px-3 hover:bg-gray-50 rounded cursor-pointer ${!currentFolderUuid ? 'bg-blue-50' : ''}`}
                onClick={() => onFolderSelect && onFolderSelect(null, null, [])}
              >
                {!currentFolderUuid && (
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500 rounded-l" />
                )}
                <div className="w-3" />
                <FileText size={16} className="text-blue-500 flex-shrink-0 mr-2" />
                <span className="text-sm font-medium cursor-pointer select-none">All Files</span>
              </div>
            </div>
          </div>
        )}

        {/* Folder Tree */}
        {loading && (
          <div className="text-center p-3">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger m-2" role="alert">
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className="p-3 pb-6" style={{ minWidth: 'max-content' }} ref={scrollContainerRef}>
            {/* Render folder tree */}
            {folders.length > 0 ? (
              folders.map((folder, index) => renderFolder(folder, 0, index === folders.length - 1, []))
            ) : (
              <div className="text-gray-500 text-center py-4 text-sm">
                No folders in this dataroom
              </div>
            )}
          </div>
        )}
      </div>

      {/* Storage Indicator - Fixed at bottom */}
      {isOwner && (
        <div className="border-t border-gray-100 bg-white flex-shrink-0" style={{ minHeight: '175px' }}>
          <div className="py-3 px-3">
            {storageStats ? (
              <StorageIndicator storageData={storageStats} />
            ) : (
              <div className="text-gray-400 text-xs">Loading storage data...</div>
            )}
          </div>
        </div>
      )}

    </div>
  );
});

FolderTreeSidebar.displayName = 'FolderTreeSidebar';

export default FolderTreeSidebar;